import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import app from 'flarum/forum/app';
import ConversationList from '../components/ConversationList';
import ConversationView from '../components/ConversationView';
import * as api from '../utils/privateMessages.mjs';

const t = (key) => app.translator.trans(`yingtao-forum-homepage.forum.private_messages.${key}`);

export default class PrivateMessagesPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.alive = true;
    this.conversations = [];
    this.messages = [];
    this.drafts = new Map();
    this.query = '';
    this.users = [];
    this.generation = 0;
    this.selectionGeneration = 0;
    this.listGeneration = 0;
    this.searchGeneration = 0;
    this.selected = m.route.param('conversation') ? String(m.route.param('conversation')) : null;
    this.loading = true;
    this.refresh();
    this.timer = setInterval(() => this.refresh(), 15000);
  }

  onremove(vnode) {
    this.alive = false;
    this.generation++;
    this.listGeneration++;
    this.searchGeneration++;
    this.selectionGeneration++;
    clearInterval(this.timer);
    super.onremove(vnode);
  }

  async refresh() {
    const token = ++this.listGeneration;
    try {
      const rows = await api.listConversations(app);
      if (!this.alive || token !== this.listGeneration) return;
      this.conversations = rows;
      this.error = null;
      if (this.selected) await this.loadMessages();
    } catch (error) {
      if (this.alive && token === this.listGeneration) this.error = api.messageErrorKey(error);
    } finally {
      if (this.alive && token === this.listGeneration) { this.loading = false; m.redraw(); }
    }
  }

  select(id) {
    this.selected = id == null ? null : String(id);
    this.selectionGeneration++;
    this.searchGeneration++;
    this.generation++;
    this.messages = [];
    this.hasMore = false;
    this.historyLoaded = false;
    this.nextBefore = null;
    this.gapUntil = null;
    this.sending = false;
    this.busy = false;
    this.loadingOlder = false;
    this.error = null;
    this.report = null;
    this.loadMessages();
  }

  async loadMessages(older = false) {
    if (!this.selected || this.loadingOlder) return;
    const id = this.selected;
    const token = ++this.generation;
    if (older) this.loadingOlder = true;
    try {
      const response = await api.listMessages(app, id, older ? this.nextBefore : null);
      if (!this.alive || token !== this.generation || id !== this.selected) return;
      let resetHistory = false;
      if (older) {
        this.messages = [...new Map([...response.messages, ...this.messages].map(row => [row.id, row])).values()]
          .sort((left, right) => left.id - right.id);
        if (this.gapUntil != null && (!response.has_more || response.messages.some(row => row.id <= this.gapUntil))) {
          this.gapUntil = null;
        }
      }
      else {
        const cutoff = response.messages[0]?.id;
        const previousLatest = this.messages.at(-1)?.id;
        resetHistory = Boolean(this.historyLoaded && response.has_more && cutoff && (!previousLatest || cutoff > previousLatest));
        if (resetHistory) {
          // A suspended tab can miss whole pages. Restart pagination at the newest
          // window and keep its unread marker until the intervening page is shown.
          this.gapUntil = this.gapUntil ?? previousLatest ?? 0;
          this.messages = response.messages;
        } else {
          this.messages = [...this.messages.filter(row => cutoff && row.id < cutoff), ...response.messages];
        }
      }
      if (older || resetHistory || !this.historyLoaded) {
        this.hasMore = response.has_more;
        this.nextBefore = response.next_before;
      }
      this.historyLoaded = true;
      const last = this.messages.at(-1);
      if (last && this.gapUntil == null) {
        await api.markRead(app, id, last.id);
        if (!this.alive || token !== this.generation || id !== this.selected) return;
        const conversation = this.conversations.find(row => String(row.id) === String(id));
        if (conversation) conversation.unread_count = 0;
      }
    } catch (error) {
      if (this.alive && token === this.generation && id === this.selected) this.error = api.messageErrorKey(error);
    } finally {
      if (this.alive && token === this.generation && id === this.selected) { this.loadingOlder = false; m.redraw(); }
    }
  }

  async search() {
    const token = ++this.searchGeneration;
    try {
      const users = await api.searchUsers(app, this.query);
      if (this.alive && token === this.searchGeneration) { this.users = users; this.hasSearched = true; }
    } catch (error) {
      if (this.alive && token === this.searchGeneration) this.error = api.messageErrorKey(error);
    } finally { if (this.alive && token === this.searchGeneration) m.redraw(); }
  }

  async create(user) {
    const token = ++this.searchGeneration;
    try {
      const row = await api.createConversation(app, user.id);
      if (!this.alive || token !== this.searchGeneration) return;
      if (!this.conversations.some(item => item.id === row.id)) this.conversations.unshift(row);
      this.users = [];
      this.select(row.id);
    } catch (error) {
      if (this.alive && token === this.searchGeneration) this.error = api.messageErrorKey(error);
    } finally { if (this.alive && token === this.searchGeneration) m.redraw(); }
  }

  async action(kind) {
    const id = this.selected;
    const token = this.selectionGeneration;
    const current = () => this.alive && id === this.selected && token === this.selectionGeneration;
    const conversation = this.conversations.find(row => String(row.id) === String(id));
    if (this.busy || this.sending || !conversation) return;
    const desiredBlocked = !conversation.blocked;
    const draft = this.drafts.get(id) || '';
    const report = this.report;
    this[kind === 'send' ? 'sending' : 'busy'] = true;
    try {
      if (kind === 'send') {
        await api.sendMessage(app, id, draft);
        if (current() && this.drafts.get(id) === draft) this.drafts.set(id, '');
      } else if (kind === 'block') {
        await api.setBlocked(app, id, desiredBlocked);
        if (current()) {
          this.listGeneration++;
          const displayed = this.conversations.find(row => String(row.id) === String(id));
          if (displayed) displayed.blocked = desiredBlocked;
        }
      } else if (report) {
        await api.reportMessage(app, report.id, this.reason, this.description);
        if (current() && report === this.report) { this.report = null; this.notice = 'report_created'; }
      }
      if (current() && kind === 'send') await this.loadMessages();
      if (current() && kind === 'send') this.scrollToLatest = true;
    } catch (error) {
      if (current()) this.error = kind === 'send' && Number(error.status) === 403 ? 'blocked_error' : api.messageErrorKey(error);
    }
    finally {
      if (current()) { this.sending = false; this.busy = false; m.redraw(); }
    }
  }

  view() {
    if (!app.session.user) return m('div.container', t('login_required'));
    const conversation = this.conversations.find(row => String(row.id) === String(this.selected));
    return m('div.container.PrivateMessagesPage', [
      m('h1', t('nav')),
      this.error && m('div.Alert.Alert--error', { role: 'alert' }, [t(this.error), Button.component({ onclick: () => this.refresh(), className: 'Button' }, t('retry'))]),
      this.notice && m('div.Alert.Alert--success', t(this.notice)),
      this.loading && LoadingIndicator.component(),
      m('div.MessageLayout', { className: this.selected ? 'MessageLayout--selected' : '' }, [
        m('aside.MessageSidebar', [
          m('form.MessageSearch', { onsubmit: event => { event.preventDefault(); this.search(); } }, [
            m('input.FormControl', { value: this.query, 'aria-label': t('search_label'), placeholder: t('search_label'),
              oninput: event => { this.query = event.target.value; this.searchGeneration++; this.users = []; this.hasSearched = false; } }),
            Button.component({ type: 'submit', className: 'Button' }, t('search')),
          ]),
          this.users.map(user => Button.component({ className: 'Button MessageSearch-user', onclick: () => this.create(user) }, user.nickname)),
          this.hasSearched && !this.users.length && m('p', t('empty_users')),
          ConversationList.component({ rows: this.conversations, selected: this.selected, onSelect: id => this.select(id) }),
        ]),
        ConversationView.component({ conversation, messages: this.messages, userId: app.session.user.id(),
          draft: this.drafts.get(this.selected) || '', sending: this.sending, busy: this.busy,
          hasMore: this.hasMore, loadingOlder: this.loadingOlder,
          onBack: () => { this.select(null); }, onOlder: () => this.loadMessages(true),
          onBlock: () => this.action('block'), onSend: () => this.action('send'),
          onDraft: text => this.drafts.set(this.selected, text),
          onReport: row => { this.report = row; this.reason = 'abuse'; this.description = ''; },
        }),
      ]),
      this.report && m('section.MessageReport.Alert', [m('h2', t('report')),
        m('select.FormControl', { value: this.reason, onchange: event => { this.reason = event.target.value; } },
          ['abuse', 'privacy', 'advertising', 'inappropriate', 'other'].map(reason => m('option', { value: reason }, t(`reason_${reason}`)))),
        m('textarea.FormControl', { value: this.description, 'aria-label': t('description'), maxlength: 2000,
          oninput: event => { this.description = event.target.value; } }),
        Button.component({ className: 'Button Button--primary', loading: this.busy, onclick: () => this.action('report') }, t('submit')),
        Button.component({ className: 'Button', onclick: () => { this.report = null; } }, t('cancel')),
      ]),
    ]);
  }

  onupdate(vnode) {
    super.onupdate(vnode);
    if (this.scrollToLatest) {
      vnode.dom.querySelector('.MessageRow:last-child')?.scrollIntoView({ block: 'nearest' });
      this.scrollToLatest = false;
    }
  }
}
